import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import ResponsiveTooltip from './ui/ResponsiveTooltip';
import { CheckSquare2, Clock, AlertTriangle, Check, X, Eye, FileText, User, Calendar, MessageSquare, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { showToast } from '../utils/toast';
import { handleAPIError } from '../utils/errorHandler';
import CommentModal from './CommentModal';
import { actionPointAPI } from '../Services/apiService';
import { truncateText } from '../lib/utils';
import Pagination from './Pagination';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import AssignedByMeTable from './AssignedByMeTable';


const ReviewTasksTable = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewComment, setReviewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewSubmissionCommentOpen, setViewSubmissionCommentOpen] = useState(false);
  const [submissionCommentText, setSubmissionCommentText] = useState('');
  const [approvingTaskId, setApprovingTaskId] = useState(null);

  // Pagination states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  const fetchReviewTasks = useCallback(async () => {
    try {
      const response = await actionPointAPI.getReviewItems({
        page,
        page_size: pageSize
      });

      const responseData = response.data;

      if (responseData?.items && responseData?.total !== undefined) {
        setTasks(responseData.items || []);
        setTotal(responseData.total || 0);
        setPages(responseData.pages || 1);
        setHasNext(responseData.has_next || false);
        setHasPrev(responseData.has_prev || false);
      } else {
        // Backward compatibility — treat response as flat array
        const tasksData = responseData?.items || [];
        setTasks(tasksData);
        setTotal(tasksData.length);
        setPages(1);
      }
    } catch (error) {
      handleAPIError(error, 'Failed to load action points for review', true);
      setTasks([]);
    }
  }, [page, pageSize]);

  // Wrapper to load tasks. When `showLoading` is true, we show the initial loading UI.
  const loadTasks = useCallback(async (showLoading = false) => {
    if (!user?.token) {
      if (showLoading) setLoading(false);
      return;
    }
    try {
      if (showLoading) setLoading(true);
      await fetchReviewTasks();
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [user?.token, fetchReviewTasks]);

  // Keep a ref to the latest loadTasks so the interval never gets a stale closure
  const loadTasksRef = useRef(loadTasks);
  useEffect(() => {
    loadTasksRef.current = loadTasks;
  });

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  useEffect(() => {
    loadTasks(true);

    const handleTaskStatusChange = () => loadTasksRef.current(false);
    window.addEventListener('taskStatusChanged', handleTaskStatusChange);
    return () => window.removeEventListener('taskStatusChanged', handleTaskStatusChange);
  }, [loadTasks]);

  // Auto-refresh every 60s — created once so it never stacks on page changes
  useEffect(() => {
    const refreshInterval = setInterval(() => loadTasksRef.current(false), 60000);
    return () => clearInterval(refreshInterval);
  }, []);

  // Backend already sorts by submitted_at, use tasks directly
  const sortedTasks = tasks;

  // Calculate analytics from tasks
  const reviewAnalytics = useMemo(() => {
    const total = tasks.length;
    const overdue = tasks.filter(task => {
      if (!task.due_date) return false;
      const dueDate = new Date(task.due_date);
      dueDate.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today > dueDate;
    }).length;
    const pending = total - overdue;

    return {
      total,
      pending,
      overdue
    };
  }, [tasks]);

  const handleApprove = async (taskId) => {
    setApprovingTaskId(taskId);
    try {
      await actionPointAPI.approveReview(taskId);
      showToast.success('Action point completed successfully');

      // CRITICAL FIX: Reset to page 1 if current page becomes empty after approval
      // This prevents showing empty page when last item on page is approved
      const currentPageItemCount = tasks.length;
      if (currentPageItemCount === 1 && page > 1) {
        // If this was the last item on the page, go to previous page
        setPage(page - 1);
      } else {
        // Otherwise, refetch current page
        await loadTasks(false);
      }

      // Trigger event for other components (MyTaskManagement, etc.)
      window.dispatchEvent(new CustomEvent('taskStatusChanged'));
    } catch (error) {
      handleAPIError(error, 'Failed to approve action point', true);
    } finally {
      setApprovingTaskId(null);
    }
  };

  const handleReject = (task) => {
    setSelectedTask(task);
    setCommentModalOpen(true);
  };

  const handleCommentSubmit = async (comment) => {
    if (!selectedTask) return;

    setIsSubmitting(true);
    try {
      await actionPointAPI.rejectReview(selectedTask.id, comment);
      showToast.success('Action point rejected');

      // CRITICAL FIX: Reset to page 1 if current page becomes empty after rejection
      // This prevents showing empty page when last item on page is rejected
      const currentPageItemCount = tasks.length;
      if (currentPageItemCount === 1 && page > 1) {
        // If this was the last item on the page, go to previous page
        setPage(page - 1);
      } else {
        // Otherwise, refetch current page
        await loadTasks(false);
      }

      // Trigger event for other components (MyTaskManagement, etc.)
      window.dispatchEvent(new CustomEvent('taskStatusChanged'));

      // Close modal and reset on success
      setCommentModalOpen(false);
      setSelectedTask(null);
    } catch (error) {
      handleAPIError(error, 'Failed to reject action point', true);
      throw error; // Re-throw to prevent clearing comment
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReview = (task) => {
    setSelectedTask(task);
    setReviewModalOpen(true);
  };

  const submitReview = async () => {
    if (!selectedTask || !reviewComment.trim()) {
      showToast.error('Please enter a review remark');
      return;
    }

    try {
      // Here you would typically call an API to submit the review comment
      // For now, we'll just close the modal
      showToast.success('Review submitted successfully');
      setReviewModalOpen(false);
      setReviewComment('');
      setSelectedTask(null);
    } catch (error) {
      showToast.error('Failed to submit review');
      console.error('Review error:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  const renderPendingTasks = () => {
    if (loading) {
      return (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckSquare2 className="h-5 w-5" />
              Tasks for Review
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="loading-shimmer h-32 rounded-lg"></div>
          </CardContent>
        </Card>
      );
    }

    if (tasks.length === 0) {
      return (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckSquare2 className="h-5 w-5" />
              Tasks for Review
            </CardTitle>
            <CardDescription>
              Tasks submitted for your review and approval
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-8">
              <CheckSquare2 className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Tasks for Review</h3>
              <p className="text-muted-foreground text-center">
                No tasks are currently submitted for review.
              </p>
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        <Card>

        <CardContent>
          {/* Desktop Table View */}
          <div className="hidden md:block border rounded-lg mt-2 overflow-hidden">
            <div className="max-h-[calc(100vh-290px)] overflow-auto">
              <div className="relative w-full overflow-visible">
                <table className="w-full caption-bottom text-sm">
                  <thead className="sticky top-0 z-10 bg-gray-100 shadow-sm [&_tr]:border-b">
                    <tr className='bg-gray-100 border-b'>
                      <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Sr. No.</th>
                      <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground">Task</th>
                      <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">
                        <div className="flex items-center justify-center gap-2">
                          Assignees
                          <User className="w-4 h-4 text-gray-400" />
                        </div>
                      </th>
                      <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">
                        <div className="flex items-center justify-center gap-2">
                          Submitted By
                          <User className="w-4 h-4 text-gray-400" />
                        </div>
                      </th>
                      <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">
                        <div className="flex items-center justify-center gap-2">
                          Approver
                          <User className="w-4 h-4 text-gray-400" />
                        </div>
                      </th>
                      <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">
                        <div className="flex items-center justify-center gap-2">
                          Due Date
                          <Calendar className="w-4 h-4 text-gray-400" />
                        </div>
                      </th>
                      <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">
                        <div className="flex items-center justify-center gap-2">
                          Submitted On
                          <Clock className="w-4 h-4 text-gray-400" />
                        </div>
                      </th>
                      <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Remark</th>
                      <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground w-10">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="[&_tr:last-child]:border-0">
                    {sortedTasks.map((task, index) => (
                      <tr key={task.id} className="border-b transition-colors hover:bg-gray-50">
                        <td className="p-2 text-center align-middle">
                          {index + 1}
                        </td>
                        <td className="p-2 text-left align-middle">
                          <div className="space-y-1">
                            {task.description && task.description.length > 20 ? (
                              <ResponsiveTooltip
                                content={<p className="max-w-xs whitespace-pre-wrap">{task.description}</p>}
                                className="max-w-md p-3"
                              >
                                <div className="font-medium cursor-pointer">
                                  {truncateText(task.description, 20)}
                                </div>
                              </ResponsiveTooltip>
                            ) : (
                              <div className="font-medium">{task.description}</div>
                            )}
                            {task.meeting?.title && (
                              task.meeting.title.length > 20 ? (
                                <ResponsiveTooltip
                                  content={<p className="max-w-xs whitespace-pre-wrap">{task.meeting.title}</p>}
                                  className="max-w-md p-3"
                                >
                                  <div className="text-sm text-muted-foreground cursor-pointer">
                                    Meeting: {truncateText(task.meeting.title, 20)}
                                  </div>
                                </ResponsiveTooltip>
                              ) : (
                                <div className="text-sm text-muted-foreground">
                                  Meeting: {task.meeting.title}
                                </div>
                              )
                            )}
                          </div>
                        </td>
                        <td className="p-2 text-center align-middle">
                          <ResponsiveTooltip
                            content={
                              <div className="space-y-1">
                                <div className="text-xs font-semibold text-gray-700 mb-1">
                                  Assignees ({task.assignees?.length || (task.assignee ? 1 : 0)}):
                                </div>
                                <div className="space-y-1 max-h-32 overflow-y-auto">
                                  {task.assignees && task.assignees.length > 0 ? (
                                    task.assignees.map((assignee, idx) => (
                                      <div
                                        key={assignee.id || idx}
                                        className="text-xs border-b border-gray-200 text-gray-600 py-0.5 px-1 rounded hover:bg-gray-100"
                                      >
                                        {assignee.full_name || assignee.email || 'Unknown'}
                                      </div>
                                    ))
                                  ) : task.assignee ? (
                                    <div className="text-xs border-b border-gray-200 text-gray-600 py-0.5 px-1 rounded hover:bg-gray-100">
                                      {task.assignee.full_name || task.assignee.email || 'Unknown'}
                                    </div>
                                  ) : (
                                    <div className="text-xs text-gray-600 py-0.5 px-1">
                                      No assignees
                                    </div>
                                  )}
                                </div>
                              </div>
                            }
                            side="top"
                            className="max-w-xs p-2 bg-white border border-gray-200"
                          >
                            <div className="text-sm text-gray-900 cursor-pointer">
                              {task.assignees && task.assignees.length > 0
                                ? truncateText(task.assignees.map(a => a.full_name).join(', '), 20)
                                : truncateText(task.assignee?.full_name || 'Unknown', 20)}
                            </div>
                          </ResponsiveTooltip>
                        </td>
                        <td className="p-2 text-center align-middle">
                          <div className="text-sm text-gray-900 font-medium">
                            {task.submitter?.full_name || 'Not specified'}
                          </div>
                        </td>
                        <td className="p-2 text-center align-middle">
                          <div className="text-sm text-gray-900">
                            {task.approver?.full_name || 'Not Assigned'}
                          </div>
                        </td>
                        <td className="p-2 text-center align-middle">
                          <div className="flex items-center justify-center gap-2">
                            {formatDate(task.due_date)}
                          </div>
                        </td>
                        <td className="p-2 text-center align-middle">
                          <div className="flex items-center justify-center gap-2">
                            {task.submitted_at ? format(new Date(task.submitted_at), 'MMM dd, yyyy HH:mm') : 'Not submitted'}
                          </div>
                        </td>
                        <td className="p-2 text-center align-middle">
                          {task.assignee_comment && (
                            <ResponsiveTooltip content={<p className="text-xs">View Comment</p>}>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSubmissionCommentText(task.assignee_comment || 'No remark available');
                                  setViewSubmissionCommentOpen(true);
                                }}
                                className="text-blue-600 hover:text-blue-700  w-9 h-9 p-0 mx-auto"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </ResponsiveTooltip>
                          )}
                        </td>
                        <td className="p-2 text-center align-middle">
                          <div className="flex flex-col items-center gap-2">
                            <div className="flex items-center justify-center gap-2">
                              {task.document_url && (
                                <ResponsiveTooltip content={<p className="text-xs">View Document</p>}>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => window.open(task.document_url, '_blank')}
                                  >
                                    <FileText className="h-4 w-4 mr-1" />
                                  </Button>
                                </ResponsiveTooltip>
                              )}
                            </div>
                            <div className="flex items-center justify-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600 hover:text-green-700"
                                onClick={() => handleApprove(task.id)}
                                disabled={approvingTaskId === task.id || isSubmitting}
                              >
                                {approvingTaskId === task.id ? (
                                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                ) : (
                                  <Check className="h-4 w-4 mr-1" />
                                )}
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleReject(task)}
                                disabled={approvingTaskId === task.id || isSubmitting}
                              >
                                <X className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Mobile Accordion View */}
          <div className="md:hidden mt-2">
            <Accordion type="single" collapsible className="w-full space-y-2">
              {sortedTasks.map((task) => (
                <AccordionItem key={task.id} value={`review-${task.id}`} className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-start justify-between w-full pr-4">
                      <div className="flex-1 text-left space-y-1">
                        {task.description && task.description.length > 20 ? (
                          <ResponsiveTooltip
                            content={<p className="max-w-xs whitespace-pre-wrap">{task.description}</p>}
                          >
                            <p className="font-medium text-gray-900 text-sm cursor-pointer">{truncateText(task.description, 20)}</p>
                          </ResponsiveTooltip>
                        ) : (
                          <p className="font-medium text-gray-900 text-sm">{task.description}</p>
                        )}
                        {task.meeting?.title && (
                          <span className="text-xs text-gray-500 truncate">{task.meeting.title}</span>
                        )}
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2 pb-4">
                    <div className="space-y-3">
                      {task.meeting?.title && (
                        <div className="flex items-start gap-2 text-sm">
                          <FileText className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                          <div>
                            <span className="text-gray-500">Meeting: </span>
                            {task.meeting.title && task.meeting.title.length > 20 ? (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button className="text-gray-900 hover:underline cursor-pointer">
                                    {truncateText(task.meeting.title, 20)}
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-72 p-3" align="start">
                                  <div className="text-sm text-gray-900 whitespace-pre-wrap">
                                    {task.meeting.title}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            ) : (
                              <span className="text-gray-900">{task.meeting.title}</span>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-sm">
                        <User className="w-4 h-4 text-gray-400 shrink-0" />
                        <div>
                          <span className="text-gray-500">Assignees: </span>
                          <span className="text-gray-900">
                            {task.assignees && task.assignees.length > 0
                              ? task.assignees.map(a => a.full_name).join(', ')
                              : task.assignee?.full_name || 'Unknown'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <User className="w-4 h-4 text-gray-400 shrink-0" />
                        <div>
                          <span className="text-gray-500">Submitted By: </span>
                          <span className="text-gray-900 font-medium">{task.submitter?.full_name || 'Not specified'}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <User className="w-4 h-4 text-gray-400 shrink-0" />
                        <div>
                          <span className="text-gray-500">Approver: </span>
                          <span className="text-gray-900">{task.approver?.full_name || 'Not Assigned'}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                        <div>
                          <span className="text-gray-500">Due Date: </span>
                          <span className="text-gray-900">{formatDate(task.due_date)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-gray-400 shrink-0" />
                        <div>
                          <span className="text-gray-500">Submitted: </span>
                          <span className="text-gray-900">
                            {task.submitted_at ? format(new Date(task.submitted_at), 'MMM dd, yyyy HH:mm') : 'Not submitted'}
                          </span>
                        </div>
                      </div>
                      <div className="pt-2   space-y-2">
                        <div className='flex items-center gap-2'>
                          {task.assignee_comment && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full "
                              onClick={() => {
                                setSubmissionCommentText(task.assignee_comment || 'No remark available');
                                setViewSubmissionCommentOpen(true);
                              }}
                            >
                              <MessageSquare className="h-4 w-4 mr-1" />

                            </Button>
                          )}
                          <ResponsiveTooltip content={<p className="text-xs">View Document</p>}>
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full"
                              disabled={!task.document_url}
                              onClick={() => window.open(task.document_url, '_blank')}
                            >
                              <FileText className="h-4 w-4 mr-1" />
                            </Button>
                          </ResponsiveTooltip>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 hover:text-green-700 flex-1"
                            onClick={() => handleApprove(task.id)}
                            disabled={approvingTaskId === task.id || isSubmitting}
                          >
                            {approvingTaskId === task.id ? (
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <Check className="h-4 w-4 mr-1" />
                            )}
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700 flex-1"
                            onClick={() => handleReject(task)}
                            disabled={approvingTaskId === task.id || isSubmitting}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Pagination */}
          {total > 0 && (
            <Pagination
              page={page}
              pages={pages}
              total={total}
              pageSize={pageSize}
              hasNext={hasNext}
              hasPrev={hasPrev}
              onPageChange={setPage}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </CardContent>
      </Card>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <Tabs defaultValue="pending" className="w-full">
        <div className="flex items-center justify-start mb-4">
          <TabsList>
            <TabsTrigger value="pending">Pending Review</TabsTrigger>
            <TabsTrigger value="assigned">Assigned By Me</TabsTrigger>
          </TabsList>
        </div>
        
        <TabsContent value="pending" className="m-0">
          {renderPendingTasks()}
        </TabsContent>
        
        <TabsContent value="assigned" className="m-0">
          <AssignedByMeTable />
        </TabsContent>
      </Tabs>

      <Dialog open={reviewModalOpen} onOpenChange={setReviewModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Task</DialogTitle>
            <DialogDescription>
              Provide feedback for: {selectedTask?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="review-comment">Review Comment</Label>
              <Textarea
                id="review-comment"
                placeholder="Enter your review remarks..."
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                rows={4}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setReviewModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={submitReview}>
                Submit Review
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <CommentModal
        open={commentModalOpen}
        onClose={() => setCommentModalOpen(false)}
        onSubmit={handleCommentSubmit}
        title="Reject Action Point"
        description="Please provide a reason for rejecting this action point"
        placeholder="Enter reason for rejection..."
        submitText="Reject"
        isLoading={isSubmitting}
      />

      <Dialog open={viewSubmissionCommentOpen} onOpenChange={() => setViewSubmissionCommentOpen(false)}>
        <DialogContent className="sm:max-w-[500px] w-[95vw]">
          <DialogHeader>
            <DialogTitle>Remark</DialogTitle>
          </DialogHeader>
          <div className="whitespace-pre-wrap text-sm text-foreground">
            {submissionCommentText || 'No remark provided'}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReviewTasksTable;